import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AIChartTooltip from "../components/AIChartTooltip";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import PlotSummary from "../components/PlotSummary";
import { useTheme, getColors } from "../ThemeContext";  
import { API } from "../config/api";
const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525", border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d", pink:"#ff4da6", purple:"#a855f7",
  blue:"#3b9eff", red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a",
  text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }  
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";
const CHART_MARGIN = { top: 8, right: 14, left: -10, bottom: 36 };
const DUAL_AXIS_MARGIN = { top: 8, right: 18, left: 8, bottom: 36 };
const BAR_CHART_MARGIN = { top: 8, right: 14, left: 8, bottom: 48 };
const LEGEND_PROPS = { verticalAlign:"top", align:"right", iconSize:8, wrapperStyle:{ fontFamily:MONO, fontSize:10, paddingBottom:6 } };
function TTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.panel, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 12px", fontFamily:MONO, fontSize:11 }}>
      <div style={{ color:C.muted, marginBottom:4 }}>{label}</div>
      {payload.map((p,i)=>(
        <div key={i} style={{ color:p.color }}>{p.name}: {typeof p.value==="number"?p.value.toLocaleString(undefined,{maximumFractionDigits:2}):p.value}</div>
      ))}
    </div>
  );
}
function Card({ title, subtitle, children, height, accentColor, summarySubtitle }) {
  const ac = accentColor || C.cyan;
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"20px 16px", position:"relative" }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      {height ? <div style={{ height }}>{children}</div> : children}
      {height && (
        <PlotSummary inline title={title} subtitle={summarySubtitle || (typeof subtitle==="string" ? subtitle : "")} accent={ac}>{children}</PlotSummary>
      )}
    </div>
  );
}
function KPI({ label, value, unit, color }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 18px" }}>
      <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color:color||C.cyan, marginTop:6 }}>
        {value == null ? "-" : value}{unit ? <span style={{ fontSize:12, color:C.muted, marginLeft:4 }}>{unit}</span> : null}
      </div>
    </div>
  );
}
function ruleStatusColor(status) {
  return { pass:C.green, marginal:C.amber, fail:C.red }[status] || C.muted;
}
function RuleStatusIcon({ status, color }) {
  if (status === "pass") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  );
  if (status === "marginal") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  );
}
function RulesPanel({ rules, accentColor }) {
  if (!rules || rules.length === 0) return null;
  const passCount = rules.filter(r=>r.status==="pass").length;
  const margCount = rules.filter(r=>r.status==="marginal").length;
  const failCount = rules.filter(r=>r.status==="fail").length;
  return (
    <Card title="Physics-Based Rules Validation" subtitle="Hard physical constraints checked against every cycle" accentColor={accentColor||C.orange}>
      <div style={{ display:"flex", gap:16, marginBottom:14 }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.green }}>◆ Pass: {passCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>▲ Marginal: {margCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.red }}>■ Fail: {failCount}</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {rules.map(r => {
          const sc = ruleStatusColor(r.status);
          return (
            <div key={r.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:4 }}>
                <RuleStatusIcon status={r.status} color={sc}/>
                <span style={{ fontFamily:SANS, fontWeight:700, fontSize:12, color:C.text }}>{r.name}</span>
                <span style={{ fontFamily:MONO, fontSize:8, letterSpacing:1, textTransform:"uppercase", color:sc, background:`${sc}18`, border:`1px solid ${sc}44`, borderRadius:3, padding:"2px 7px" }}>{r.status}</span>
                {r.equation && <span style={{ fontFamily:MONO, fontSize:9, color:C.cyan, background:`${C.cyan}12`, border:`1px solid ${C.cyan}33`, borderRadius:3, padding:"2px 7px" }}>{r.equation}</span>}
                <span style={{ fontFamily:MONO, fontSize:10, color:C.cyan, marginLeft:"auto" }}>{r.value}</span>
              </div>
              <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.6 }}>{r.explanation}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
export default function InjectionDashboardPage() {
  const { isDark } = useTheme();          
  applyTheme(getColors(isDark));         
  const [wells, setWells] = useState([]);
  const [selectedWell, setSelectedWell] = useState("all");
  const [method, setMethod] = useState("all");
  const [data, setData] = useState(null);
  const [physics, setPhysics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/injection/wells`)
      .then(r => setWells(r.data.wells || []))
      .catch(() => setWells([]));
  }, []);
  const load = useCallback(() => {
    setLoading(true); setError(null);
    Promise.all([
      axios.get(`${API}/injection/dashboard`, { params: { well: selectedWell, method } }),
      axios.get(`${API}/injection/physics-validation`, { params: { method } }),
    ])
      .then(([r, p]) => { setData(r.data); setPhysics(p.data); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [selectedWell, method]);
  useEffect(() => { load(); }, [load]);
  const timeline = data?.timeline || [];
  const byWell = data?.by_well || [];
  const kpis = data?.kpis || {};
  const valvePie = [
    { name: "Open", value: kpis.valve_open_pct ?? 0, color: C.green },
    { name: "Closed", value: 100 - (kpis.valve_open_pct ?? 0), color: C.red },
  ];
  return (
    <div style={{ fontFamily:SANS }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", gap:10 }}>
        <select value={method} onChange={e=>setMethod(e.target.value)} style={selStyle()}>
            <option value="all">All Methods</option>
            <option value="GAPL">GAPL</option>
            <option value="PAGL">PAGL</option>
            <option value="GASLIFT">Gas Lift</option>
          </select>
          <select value={selectedWell} onChange={e=>setSelectedWell(e.target.value)} style={selStyle()}>
            <option value="all">All Wells</option>
            {wells.map(w => <option key={w.well_id} value={w.well_id}>{w.well_id}</option>)}
          </select>
        </div>
      </div>
      {loading && <div style={{ color:C.muted, fontFamily:MONO }}>Loading…</div>}
      {error && <div style={{ color:C.red, fontFamily:MONO }}>{error}</div>}
      {!loading && !error && data && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:14, marginBottom:20 }}>
            <KPI label="Injection Rate" value={kpis.current_injection_rate_mcfd?.mean} unit="MCFD" color={C.cyan}/>
            <KPI label="Injection Pressure" value={kpis.injection_pressure_psi?.mean} unit="psi" color={C.amber}/>
            <KPI label="Cumulative Volume" value={kpis.cumulative_injected_volume_mcf?.toLocaleString()} unit="MCF" color={C.teal}/>
            <KPI label="Valve Open %" value={kpis.valve_open_pct} unit="%" color={C.green}/>
            <KPI label="Active Wells" value={kpis.n_wells} color={C.purple}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:18, marginBottom:18 }}>
            <Card title="Injection Rate &amp; Pressure Over Time" summarySubtitle="Gas injection rate (MCFD, left axis, area) and injection pressure (psi, right axis, line) over time; judge whether pressure and rate move together, any drops or spikes, and stability of injection" accentColor={C.cyan} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline} margin={DUAL_AXIS_MARGIN}>
                  <defs>
                    <linearGradient id="gInjRate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={C.cyan} stopOpacity={0.4}/><stop offset="95%" stopColor={C.cyan} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                  <XAxis
                    dataKey="Timestamp"
                    tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }}
                    tickLine={false}
                    minTickGap={40}
                    tickFormatter={(v) => (v && v !== "NaT" ? v.slice(5, 16) : "")}
                    label={{ value: "Timestamp", position: "insideBottom", offset: -18, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  />
                  <YAxis
                    yAxisId="left"
                    width={48}
                    tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                    label={{ value: "Injection Rate (MCFD)", angle: -90, position: "insideLeft", dx: -4, dy: 40, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={48}
                    tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                    label={{ value: "Pressure (psi)", angle: 90, position: "insideRight", dx: 4, dy: 20, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  />
                  <Tooltip
                    trigger="hover"
                    isAnimationActive={false}
                    content={<AIChartTooltip fieldKey="InjectionRate_MCFD" contextData={{ chart:"Injection Rate & Pressure Over Time" }} />}
                  />
                  <Legend {...LEGEND_PROPS}/>
                  <Area yAxisId="left" type="monotone" dataKey="InjectionRate_MCFD" name="Rate (MCFD)" stroke={C.cyan} fill="url(#gInjRate)" strokeWidth={2} dot={false}/>
                  <Line yAxisId="right" type="monotone" dataKey="InjectionPressure_psi" name="Pressure (psi)" stroke={C.amber} strokeWidth={1.5} dot={false}/>
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Valve Status" subtitle="Share of records" summarySubtitle="Share of injection records where the gas injection valve was open versus closed" accentColor={C.green} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={valvePie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {valvePie.map((e,i)=><Cell key={i} fill={e.color}/>)}
                  </Pie>
                  <Tooltip
                    trigger="hover"
                    isAnimationActive={false}
                    content={<AIChartTooltip fieldKey="ValveOpenPct" contextData={{ chart:"Valve Status" }} />}
                  />
                  <Legend {...LEGEND_PROPS} verticalAlign="bottom" align="center"/>
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <Card title="Cumulative Injected Volume by Well" summarySubtitle="Total gas injected (MCF) by each well, compared across wells; look for wells injecting far more or less than the others" accentColor={C.teal} height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byWell} margin={BAR_CHART_MARGIN}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis
                  dataKey="Well_ID"
                  tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }}
                  tickLine={false}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                  label={{ value: "Well", position: "insideBottom", offset: -10, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                />
                <YAxis
                  width={48}
                  tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                  label={{ value: "Cumulative Volume (MCF)", angle: -90, position: "insideLeft", dx: -4, dy: 50, fill: C.muted, fontSize: 9, fontFamily: MONO }}
                />
                <Tooltip
                  trigger="hover"
                  isAnimationActive={false}
                  content={<AIChartTooltip fieldKey="cumulative_volume_mcf" contextData={{ chart:"Cumulative Injected Volume by Well" }} />}
                />
                <Bar dataKey="cumulative_volume_mcf" name="Cumulative Volume (MCF)" fill={C.teal} radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <div style={{ marginTop:18 }}><RulesPanel rules={physics?.rules} accentColor={C.orange}/></div>
        </>
      )}
    </div>
  );
}
function selStyle() {                     
  return {
    background:C.panel, color:C.text, border:`1px solid ${C.border2}`, borderRadius:8,
    padding:"8px 12px", fontFamily:MONO, fontSize:11, cursor:"pointer",
  };
}